<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category, Tag } from '~/models/content'
import { useMembershipStore } from '~/stores/membership'
import { useOpenPDFViewer } from '~/composables/open-pdf-viewer'
import { useDownloadFile } from '~/composables/download'

const { callToAction, isDownloading, downloadProgress, cancelDownload } =
  useDownloadFile()
const membershipStore = useMembershipStore()
const { $cloudinary } = useNuxtApp()
const { openPDFViewer } = useOpenPDFViewer()

const props = defineProps({
  id: {
    type: String,
    require: true,
  },
  linkFlipHtml5: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
  summary: {
    type: String,
    required: true,
  },
  issue: {
    type: String,
    required: true,
  },
  quarter: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  coverUrl: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    required: true,
  },
  keepMobileLayout: {
    type: Boolean,
    default: false,
  },
  subscriptionRequired: {
    type: String,
    default: undefined,
  },
})

const coverPosition = useCoverPosition(props.coverUrl)
const isAuthorized = computed(() => membershipStore.isSubscriberActive)
const subscriptionRequiredData = computed<string | undefined>(
  () => props.subscriptionRequired || undefined,
)
function onClick() {
  callToAction.value.callback(
    props.id,
    'magazine',
    props.fileName,
    subscriptionRequiredData.value,
  )
}
</script>

<template>
  <article
    class="card-hover group relative bg-s-100 p-6 pb-3"
    data-testid="magazine-card"
  >
    <div class="flex gap-4 md:gap-10">
      <div
        class="relative h-[116px] w-[84px] shrink-0 md:h-[190px] md:w-[138px]"
      >
        <img
          width="140"
          height="200"
          class="h-full w-full object-cover"
          :class="coverPosition"
          :src="$cloudinary.resize(coverUrl, 'magazine-thumbnail')"
          alt=""
        />

        <div class="absolute top-0 h-full w-full border-2 border-black/10" />
      </div>
      <div
        class="flex grow flex-col gap-2"
        :class="{ 'md:flex-row md:gap-10': keepMobileLayout }"
      >
        <div class="shrink-0">
          <p
            class="text-base font-semibold"
            data-testid="magazine-card-quarter"
          >
            {{ quarter }}
          </p>
          <p class="text-xs" data-testid="magazine-card-issue">{{ issue }}</p>
        </div>

        <div>
          <p class="text-base font-semibold" data-testid="magazine-card-title">
            {{ title }}
          </p>
          <p
            class="mt-2 text-sm"
            data-testid="magazine-card-summary"
            v-html="summary"
          ></p>
          <div v-if="pills.length" class="mt-4 flex flex-wrap gap-2">
            <div v-for="pill in pills" :key="pill.id">
              <LinkedPill v-if="pill" :slug="pill.slug" variant="black">
                {{ pill.name }}
              </LinkedPill>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="relative mt-6 flex h-10 max-h-[32px] items-center justify-center gap-2 md:mt-2 md:justify-end"
    >
      <BaseButton
        v-if="linkFlipHtml5 && isAuthorized"
        variant="primary"
        class="w-full max-w-[140px] grow border-2 border-black !bg-black !text-white md:max-w-[110px] md:grow-0"
        data-testid="view-pdf"
        @click="
          openPDFViewer(
            id,
            title,
            linkFlipHtml5,
            'magazine',
            subscriptionRequiredData,
          )
        "
      >
        View
      </BaseButton>
      <div
        class="flex w-full items-center justify-center"
        :class="[
          linkFlipHtml5 ? 'w-[140px] md:w-[110px]' : 'w-full md:w-[110px]',
        ]"
        @click="onClick"
      >
        <CircularProgressBar
          v-if="isDownloading || downloadProgress"
          size="45px"
          color="black"
          :progress="downloadProgress"
          :cancel-call-back="cancelDownload"
          :is-loading="isDownloading"
        />

        <BaseButton
          v-else
          variant="tertiary"
          class="z-10 w-full grow md:grow-0"
          data-testid="call-to-action"
        >
          {{ callToAction.text }}
        </BaseButton>
      </div>
    </div>
  </article>
</template>
